/**
 * Date utility functions for consistent date handling across the application
 * These functions handle timezone issues by working with local dates instead of UTC
 */

/**
 * Get local date string in YYYY-MM-DD format
 * This avoids timezone issues by using local date components instead of UTC
 */
export function getLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get local date from a date string (YYYY-MM-DD)
 * Creates a date at midnight in local timezone
 */
export function getLocalDateFromString(dateString: string): Date {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Check if two dates are the same calendar day (ignoring time)
 */
export function isSameDay(date1: Date, date2: Date): boolean {
  return getLocalDateString(date1) === getLocalDateString(date2);
}

/**
 * Format date as dd/mm/yyyy
 * @param date - Date object, string, number (timestamp), null, or undefined
 * @returns Formatted date string in dd/mm/yyyy format, or empty string if date is invalid/null/undefined
 */
export function formatDate(date: Date | string | number | null | undefined): string {
  if (date === null || date === undefined) {
    return '';
  }
  
  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  
  if (!d || isNaN(d.getTime())) {
    return '';
  }
  
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  
  return `${day}/${month}/${year}`;
}

/**
 * Format date and time as dd/mm/yyyy, hh:mm:ss
 * @param date - Date object, string, number (timestamp), null, or undefined
 * @returns Formatted date and time string in dd/mm/yyyy, hh:mm:ss format, or empty string if date is invalid/null/undefined
 */
export function formatDateTime(date: Date | string | number | null | undefined): string {
  if (date === null || date === undefined) {
    return '';
  }
  
  const d = typeof date === 'string' || typeof date === 'number' ? new Date(date) : date;
  
  if (!d || isNaN(d.getTime())) {
    return '';
  }
  
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const seconds = String(d.getSeconds()).padStart(2, '0');
  
  return `${day}/${month}/${year}, ${hours}:${minutes}:${seconds}`;
}
